import { KeyboardTabOutlined, KeyboardBackspaceOutlined } from '@mui/icons-material';
import { Button } from '@mui/material';
import { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';

import './styles.scss';

// Resizable-width plumbing. The open width lives in a CSS var on :root so both
// the sidebar's own width AND each consumer's content offset read the same
// value and reflow together. Persisted so the choice survives reloads / route
// changes. Only consumers that pass `resizable` show the drag handle.
const WIDTH_VAR = '--pp-sidebar-open-width';
const STORAGE_KEY = 'diletta:pp-sidebar-width';
const DEFAULT_WIDTH = 285;
const MIN_WIDTH = 240;
const MAX_WIDTH = 560;

const clampWidth = (value: number) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, value));

const applyWidthVar = (width: number | null) => {
  if (width === null) {
    document.documentElement.style.removeProperty(WIDTH_VAR);
  } else {
    document.documentElement.style.setProperty(WIDTH_VAR, `${width}px`);
  }
};

type PortfolioSidebarProps = PropsWithChildren<{
  isPortfolioSidebarOpen: boolean;
  setIsPortfolioSidebarOpen: (value: boolean) => void;
  // Heading shown when the rail is open. Defaults to the Portfolio Performance
  // wording; other consumers (e.g. the backtesting sidebar) pass their own.
  title?: string;
  // Opt-in: show a right-edge drag handle to resize the open rail. The width is
  // shared (one var, one storage key) across every consumer that opts in.
  resizable?: boolean;
}>;

export const PortfolioSidebar = ({
  children,
  isPortfolioSidebarOpen,
  setIsPortfolioSidebarOpen,
  title = 'Account selection',
  resizable = false,
}: PortfolioSidebarProps) => {
  const navRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);

  // Hydrate the persisted width once, for any resizable consumer.
  useEffect(() => {
    if (!resizable) return;
    const stored = Number(localStorage.getItem(STORAGE_KEY));
    if (Number.isFinite(stored) && stored > 0) {
      applyWidthVar(clampWidth(stored));
    }
  }, [resizable]);

  const handlePortfolioSidebarOpen = useCallback(() => {
    setIsPortfolioSidebarOpen(true);
  }, []);

  const handlePortfolioSidebarClose = useCallback(() => {
    setIsPortfolioSidebarOpen(false);
  }, []);

  const startResize = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    const navLeft = navRef.current?.getBoundingClientRect().left ?? 0;
    let latestWidth = clampWidth(navRef.current?.getBoundingClientRect().width ?? DEFAULT_WIDTH);

    setIsResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = (e: MouseEvent) => {
      latestWidth = clampWidth(e.clientX - navLeft);
      applyWidthVar(latestWidth);
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setIsResizing(false);
      localStorage.setItem(STORAGE_KEY, String(latestWidth));
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  // Double-click the handle to reset to the default width.
  const resetWidth = useCallback(() => {
    applyWidthVar(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return (
    <div className="portfolio-sidebar-container">
      <div
        ref={navRef}
        className={`portfolio-sidebar-nav ${isPortfolioSidebarOpen ? 'portfolio-sidebar-open' : 'portfolio-sidebar-close'} ${resizable ? 'portfolio-sidebar-nav--resizable' : ''} ${isResizing ? 'portfolio-sidebar-resizing' : ''}`}
        role="navigation"
        aria-hidden={!isPortfolioSidebarOpen}
      >
        <div className="portfolio-sidebar-header">
          {isPortfolioSidebarOpen && <h2 className="portfolio-sidebar-header-title">{title}</h2>}

          <div className={`portfolio-sidebar-button-${isPortfolioSidebarOpen ? 'close' : 'open'}`}>
            <Button
              onClick={
                isPortfolioSidebarOpen ? handlePortfolioSidebarClose : handlePortfolioSidebarOpen
              }
            >
              {isPortfolioSidebarOpen ? <KeyboardBackspaceOutlined /> : <KeyboardTabOutlined />}
            </Button>
          </div>
        </div>
        {/* Only mount the content when open — the 75px collapsed rail can't show
            it, and rendering the full-text list into it just clips awkwardly. */}
        {isPortfolioSidebarOpen && <div className="portfolio-sidebar-content">{children}</div>}

        {resizable && isPortfolioSidebarOpen && (
          <div
            className="portfolio-sidebar-resize-handle"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize sidebar"
            onMouseDown={startResize}
            onDoubleClick={resetWidth}
          />
        )}
      </div>
    </div>
  );
};
